/**
 * Admin — Comment Moderation (DEV-727)
 *
 * Review queue for reported comments. Lists open content flags with comment
 * context and lets a system admin remove the comment, mark the flag reviewed,
 * or dismiss it. System-admin only; English-only per CLAUDE.md.
 *
 * Backed by:
 *   GET   /api/comments/moderation/flags?status=open
 *   PATCH /api/comments/flags/{flagId}        (reviewed | dismissed)
 *   DELETE /api/comments/{commentId}          (soft-delete = remove)
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchModerationFlags,
  resolveFlag,
  deleteComment,
  type CommentFlag,
} from '../../api/comments';
import styles from './AdminPages.module.css';

export default function AdminModeration() {
  const [flags, setFlags] = useState<CommentFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFlags(await fetchModerationFlags('open'));
    } catch {
      setFlags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = useCallback(
    async (id: string, fn: () => Promise<void>) => {
      setBusyId(id);
      try {
        await fn();
        await load();
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Comment Moderation</h1>
          <p className={styles.pageSubtitle}>
            Review reported comments. {flags.length} open report{flags.length === 1 ? '' : 's'}.
          </p>
        </div>
        <div className={styles.pageActions}>
          <button className={styles.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className={styles.pageSubtitle}>Loading…</p>
      ) : flags.length === 0 ? (
        <p className={styles.emptyMsg}>No open reports.</p>
      ) : (
        <section className={styles.section}>
          {flags.map((f) => (
            <div key={f.id} className={styles.editRow}>
              <div className={styles.editRowFields}>
                <div style={{ fontWeight: 500 }}>
                  {f.commentDeleted ? '[comment already removed]' : f.commentBody}
                </div>
                <div className={styles.sectionHint}>
                  {f.flagType}
                  {f.reason ? ` — ${f.reason}` : ''} · {f.anchorType} · reported{' '}
                  {new Date(f.createdAt).toLocaleString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {!f.commentDeleted && (
                  <button
                    className={styles.btnSecondary}
                    disabled={busyId === f.id}
                    onClick={() =>
                      act(f.id, async () => {
                        await deleteComment(f.commentId);
                        await resolveFlag(f.id, 'reviewed');
                      })
                    }
                  >
                    Remove comment
                  </button>
                )}
                <button
                  className={styles.btnSecondary}
                  disabled={busyId === f.id}
                  onClick={() => act(f.id, () => resolveFlag(f.id, 'reviewed'))}
                >
                  Mark reviewed
                </button>
                <button
                  className={styles.btnSecondary}
                  disabled={busyId === f.id}
                  onClick={() => act(f.id, () => resolveFlag(f.id, 'dismissed'))}
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
